/**
 * Every matchup of the week, at a glance.
 *
 * The Board's Match tab showed one round at a time behind a dropdown, so "who
 * has Thomas got tomorrow?" cost a tap, a scroll through a listbox and losing
 * the round you were reading. The week is nine rounds and twenty-odd contests;
 * it fits on one scrolling screen, and glanceable is the whole point of a
 * board.
 *
 * This replaces the dropdown rather than sitting beside it: each round's
 * header pins that round, which is everything the dropdown did, in the same
 * place the answer appears.
 */

import { formatName, matchNumber, shortCourse } from '../engine/naming'
import type { Match, Tournament } from '../engine/types'
import { useState } from 'react'
import { contestOrder, matchesInPlayOrder, playerName, roundDay, type TournamentView } from '../select'

export function WeekMatches({
  doc,
  shownId,
  me,
  onPin,
  view,
}: {
  doc: Tournament
  /** The match the board above is showing, marked so the list reads as its index. */
  shownId?: string
  me: string
  onPin: (matchId: string) => void
  /** The scored week, so a played round can show what happened. */
  view?: TournamentView
}) {
  const matches = matchesInPlayOrder(doc)
  if (matches.length === 0) return null

  return (
    <div className="border-t border-rule">
      <h2 className="bc-label text-mute px-4 pt-4 pb-1">The week&rsquo;s matches</h2>
      {matches.map((m) => (
        <RoundBlock
          key={m.id}
          doc={doc}
          match={m}
          shown={m.id === shownId}
          me={me}
          onPin={() => onPin(m.id)}
          view={view}
        />
      ))}
    </div>
  )
}

function RoundBlock({
  doc,
  match,
  shown,
  me,
  onPin,
  view,
}: {
  doc: Tournament
  match: Match
  shown: boolean
  me: string
  onPin: () => void
  view?: TournamentView
}) {
  const round = doc.rounds.find((r) => r.id === match.roundId)
  if (!round) return null
  const course = doc.courses.find((c) => c.id === round.courseId)
  const groups = doc.groups
    .filter((g) => g.roundId === round.id)
    .sort((a, b) => a.index - b.index)

  /*
   * Contests in draw order, foursome by foursome, read off the same pairings
   * the scorer's card uses. A field-ranked round (quota, the championship) has
   * no pairings, and saying so beats a blank: "against the field" is the
   * answer to the question this list exists for.
   */
  const lines = groups.flatMap((g) =>
    contestOrder(
      g.playerIds,
      doc.pairings.filter((p) => p.groupId === g.id && p.matchId === match.id),
    ).contests.map((c) => ({
      id: c.pairing.id,
      mine: c.playerIds.includes(me),
      sides: c.pairing.sides.map((sd) =>
        sd.playerIds.map((x) => playerName(doc, x)).join(' & '),
      ),
    })),
  )

  const n = matchNumber(match)
  const courseLine = `${shortCourse(course?.name)}${
    round.nine === 'front' ? ' — front' : round.nine === 'back' ? ' — back' : ''
  }`

  /*
   * A round that has been played folds itself away — Scott's ask. The list
   * exists to answer "who have I got", and once a match is final that
   * question is settled: what is left is the RESULT, which belongs behind a
   * tap rather than down six rows of a scroll. Open by default while there
   * is golf to come; tap the chevron for what happened. Expanded, the
   * matchups carry their scorelines and the winner is in ink.
   */
  const entry = view?.byMatch[match.id]
  const done = Boolean(view?.finalMatchIds.has(match.id))
  const [open, setOpen] = useState(!done)
  const resultFor = (pairingId: string): { a: string; b: string; lead: 0 | 1 | -1 } | null => {
    if (!entry) return null
    const p = doc.pairings.find((x) => x.id === pairingId)
    if (!p) return null
    const ta = entry.tabs.find((t) => t.unitId === p.sides[0]!.id)
    const tb = entry.tabs.find((t) => t.unitId === p.sides[1]!.id)
    if (!ta || !tb) return null
    return {
      a: ta.chip.primary,
      b: tb.chip.primary,
      lead: ta.rankValue > tb.rankValue ? 1 : tb.rankValue > ta.rankValue ? -1 : 0,
    }
  }

  return (
    <div data-week-round={match.id} className="border-b border-rule">
      {/*
        The header is the control. Tapping it pins this round on the board
        above, which is everything the old dropdown did — and unlike a listbox
        it shows what you would be pinning before you commit.

        Dressed like the week view's SessionHead — Scott's redesign: the
        course is what a scanning eye hunts, so it is the big line; the game
        is the second question and wears the signal color; the day and match
        number are bookkeeping, small, on the right. Scaled one step down
        from the week's masthead because this repeats twelve times in a list,
        and pinned rows go to ink, where signal red cannot live.
      */}
      <button
        type="button"
        onClick={onPin}
        aria-pressed={shown}
        className={`bc-tap w-full text-left px-4 py-2 flex items-start justify-between gap-3 ${
          shown ? 'bg-ink text-paper' : 'bg-wash'
        }`}
      >
        <span className="min-w-0">
          <span className="bc-display text-[17px] leading-tight block truncate">
            {courseLine}
          </span>
          <span
            className={`bc-label text-[12px] block mt-0.5 ${
              shown ? 'text-paper/70' : 'text-signal'
            }`}
          >
            {formatName(match.config)}
          </span>
        </span>
        <span className="text-right shrink-0 pt-0.5">
          <span className="text-[13px] block">{roundDay(round.date)}</span>
          {n && (
            <span className={`text-[12px] block mt-0.5 ${shown ? 'opacity-70' : 'text-mute'}`}>
              M{n}
            </span>
          )}
        </span>
      </button>
      {/*
        The fold lives on its own control, not on the header: the header
        already pins the round to the board above, and one tap cannot
        honestly mean two things.
      */}
      {done && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="bc-tap w-full px-4 py-1.5 flex items-center justify-between gap-2 text-[13px] text-mute border-t border-rule"
        >
          <span>{open ? 'Played — hide the results' : 'Played — show the results'}</span>
          <span aria-hidden className="text-[11px]">{open ? '\u25b4' : '\u25be'}</span>
        </button>
      )}
      {!open ? null : lines.length === 0 ? (
        <p className="px-4 py-2 text-[13px] text-mute">
          {groups.length === 0
            ? 'No draw yet.'
            : match.config.kind === 'quota' && match.config.rankingScope === 'group'
              ? 'Everyone against their own foursome — no head-to-heads.'
              : 'Everyone against the field — no head-to-heads.'}
        </p>
      ) : (
        <ul className="px-4 py-1.5">
          {lines.map((c) => (
            <li
              key={c.id}
              {...(c.mine ? { 'data-week-mine': '' } : {})}
              className={`py-1 text-sm leading-snug ${c.mine ? 'bc-name' : 'text-mute'}`}
            >
              {/*
                A fixed two-column "A v B" rather than one wrapping string, so
                the v's line up down the page and the eye can walk one side.
              */}
              {(() => {
                const r = resultFor(c.id)
                return (
                  <>
                    <span
                      className={`inline-block w-[calc(50%-0.75rem)] text-right align-top ${
                        r && r.lead < 0 ? 'text-dim' : ''
                      }`}
                    >
                      {c.sides[0]}
                      {r && (
                        <span className={`bc-num ml-1.5 ${r.lead > 0 ? 'text-ink' : 'text-mute'}`}>
                          {r.a}
                        </span>
                      )}
                    </span>
                    <span
                      className={`inline-block w-6 text-center align-top ${c.mine ? 'text-mute' : ''}`}
                    >
                      v
                    </span>
                    <span
                      className={`inline-block w-[calc(50%-0.75rem)] align-top ${
                        r && r.lead > 0 ? 'text-dim' : ''
                      }`}
                    >
                      {c.sides[1]}
                      {r && (
                        <span className={`bc-num ml-1.5 ${r.lead < 0 ? 'text-ink' : 'text-mute'}`}>
                          {r.b}
                        </span>
                      )}
                    </span>
                  </>
                )
              })()}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
